import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { 
  Car, Users, Calendar, Shield, AlertTriangle, CheckCircle, 
  X, ChevronLeft, LogOut, Menu, DollarSign, Eye, FileText, Phone, Mail, User, ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';

const CHARGE_TYPE_LABELS = {
  excess_km: 'Excess KM',
  fuel_shortfall: 'Fuel Shortfall',
  cleaning_fee: 'Cleaning Fee',
  toll_charges: 'Toll Charges',
  damage_estimate: 'Damage Estimate'
};

const AdminDashboard = () => {
  const { user, api, logout, isSuperAdmin, isPlatformOwner, getAdminRole, hasAdminAccess, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('verifications');
  const [verifications, setVerifications] = useState([]);
  const [users, setUsers] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [disputes, setDisputes] = useState([]);
  const [pendingCharges, setPendingCharges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showChargeRejectModal, setShowChargeRejectModal] = useState(false);
  const [showEvidenceModal, setShowEvidenceModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [chargeRejectReason, setChargeRejectReason] = useState('');
  const [selectedCharge, setSelectedCharge] = useState(null);
  const [selectedChargeItem, setSelectedChargeItem] = useState(null);
  const [evidenceUrls, setEvidenceUrls] = useState([]);
  const [actionLoading, setActionLoading] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  // Part 5: Collections state
  const [failedCharges, setFailedCharges] = useState([]);
  const [collectionsSummary, setCollectionsSummary] = useState(null);
  // Admin User Details State
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserDetails, setShowUserDetails] = useState(false);
  const [featureSaving, setFeatureSaving] = useState(false);

  const updateUserFeatures = async (userId, payload) => {
    setFeatureSaving(true);
    try {
      const res = await api.patch(`/admin/users/${userId}/features`, payload);
      setSelectedUser(prev => prev ? { ...prev, subscription_tier: res.data.subscription_tier, feature_flags: res.data.feature_flags } : prev);
      toast.success('Features updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update features');
    } finally {
      setFeatureSaving(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'verifications') {
        const res = await api.get('/admin/verifications');
        setVerifications(res.data.verifications || []);
      } else if (activeTab === 'users') {
        const res = await api.get('/admin/users');
        setUsers(res.data.users || []);
      } else if (activeTab === 'bookings') {
        const res = await api.get('/admin/bookings');
        setBookings(res.data.bookings || []);
      } else if (activeTab === 'disputes') {
        const res = await api.get('/admin/disputes');
        setDisputes(res.data.disputes || []);
      } else if (activeTab === 'charges') {
        const res = await api.get('/admin/pending-charge-approvals');
        setPendingCharges(res.data.pending_charges || []);
      } else if (activeTab === 'collections') {
        const [chargesRes, summaryRes] = await Promise.all([
          api.get('/admin/collections/failed-charges'),
          api.get('/admin/collections/summary')
        ]);
        setFailedCharges(chargesRes.data.charges || []);
        setCollectionsSummary(summaryRes.data);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApproveVerification = async (verificationId) => {
    setActionLoading(true);
    try {
      await api.post(`/admin/verifications/${verificationId}/approve`);
      toast.success('Verification approved');
      fetchData();
    } catch (error) {
      toast.error('Failed to approve verification');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRejectVerification = async () => {
    if (!selectedItem) return;
    setActionLoading(true);
    try {
      await api.post(`/admin/verifications/${selectedItem.verification_id}/reject`, {
        reason: rejectReason
      });
      toast.success('Verification rejected');
      setShowRejectModal(false);
      setSelectedItem(null);
      setRejectReason('');
      fetchData();
    } catch (error) {
      toast.error('Failed to reject verification');
    } finally {
      setActionLoading(false);
    }
  };

  // P0 Part 3: Charge approval handlers
  const handleApproveChargeItem = async (chargeId, itemId) => {
    setActionLoading(true);
    try {
      await api.post(`/admin/charges/${chargeId}/item/${itemId}/approve`, {
        notes: 'Approved by admin'
      });
      toast.success('Charge item approved');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve charge');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRejectChargeItem = async () => {
    if (!selectedCharge || !selectedChargeItem) return;
    if (chargeRejectReason.length < 10) {
      toast.error('Rejection reason must be at least 10 characters');
      return;
    }
    setActionLoading(true);
    try {
      await api.post(`/admin/charges/${selectedCharge.charge_id}/item/${selectedChargeItem.item_id}/reject`, {
        reason: chargeRejectReason
      });
      toast.success('Charge item rejected');
      setShowChargeRejectModal(false);
      setSelectedCharge(null);
      setSelectedChargeItem(null);
      setChargeRejectReason('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject charge');
    } finally {
      setActionLoading(false);
    }
  };

  const handleApproveAllCharges = async (chargeId) => {
    setActionLoading(true);
    try {
      await api.post(`/admin/charges/${chargeId}/approve-all`, {
        notes: 'Bulk approved by admin'
      });
      toast.success('All charges approved');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve charges');
    } finally {
      setActionLoading(false);
    }
  };

  // P0 Part 4: Execute Stripe payment for approved charges
  const handleExecutePayment = async (chargeId) => {
    setActionLoading(true);
    try {
      const res = await api.post(`/admin/charges/${chargeId}/execute-payment`);
      if (res.data.status === 'paid') {
        toast.success(`Payment successful! Receipt: ${res.data.receipt_url || 'Check payment dashboard'}`);
      } else if (res.data.status === 'already_charged') {
        toast.info('Payment was already processed');
      } else {
        toast.success('Payment initiated');
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Payment execution failed');
    } finally {
      setActionLoading(false);
    }
  };

  // P0 Part 4: Retry failed payment
  const handleRetryPayment = async (chargeId) => {
    setActionLoading(true);
    try {
      const res = await api.post(`/admin/charges/${chargeId}/retry-payment`);
      if (res.data.status === 'paid') {
        toast.success('Payment retry successful!');
      } else {
        toast.success('Payment retry initiated');
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Payment retry failed');
    } finally {
      setActionLoading(false);
    }
  };

  // P0 Part 5: Collections handlers
  const handleCollectionRetryNow = async (chargeId) => {
    setActionLoading(true);
    try {
      const res = await api.post(`/admin/collections/${chargeId}/retry-now`);
      if (res.data.success) {
        toast.success('Retry successful!');
      } else {
        toast.info(`Retry attempted: ${res.data.status}`);
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Retry failed');
    } finally {
      setActionLoading(false);
    }
  };

  const handleMarkResolved = async (chargeId) => {
    const reason = window.prompt('Enter resolution reason (min 10 chars):');
    if (!reason || reason.length < 10) {
      toast.error('Reason required (min 10 characters)');
      return;
    }
    setActionLoading(true);
    try {
      await api.post(`/admin/collections/${chargeId}/mark-resolved`, { reason });
      toast.success('Charge marked as resolved');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark resolved');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSendToCollections = async (chargeId) => {
    if (!isPlatformOwner) {
      toast.error('Only Platform Owner can send to collections');
      return;
    }
    const reason = window.prompt('Enter reason for sending to collections:');
    if (!reason || reason.length < 10) {
      toast.error('Reason required (min 10 characters)');
      return;
    }
    setActionLoading(true);
    try {
      await api.post(`/admin/collections/${chargeId}/send-to-collections`, { reason });
      toast.success('Charge sent to collections');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed');
    } finally {
      setActionLoading(false);
    }
  };

  const handleFreezeForDispute = async (chargeId) => {
    const reason = window.prompt('Enter dispute reason:');
    if (!reason) return;
    setActionLoading(true);
    try {
      await api.post(`/admin/collections/${chargeId}/freeze-dispute`, { reason });
      toast.success('Charge frozen for dispute');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to freeze');
    } finally {
      setActionLoading(false);
    }
  };

  const handleUnfreezeCharge = async (chargeId) => {
    const resumeRetries = window.confirm('Resume automatic retries?');
    setActionLoading(true);
    try {
      await api.post(`/admin/collections/${chargeId}/unfreeze`, { resume_retries: resumeRetries });
      toast.success('Charge unfrozen');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unfreeze');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSuspendUser = async (userId) => {
    if (!isPlatformOwner) {
      toast.error('Only Platform Owner can suspend users');
      return;
    }
    const reason = window.prompt('Enter suspension reason:');
    if (!reason || reason.length < 10) {
      toast.error('Reason required (min 10 characters)');
      return;
    }
    setActionLoading(true);
    try {
      await api.post(`/admin/users/${userId}/suspend`, { reason });
      toast.success('User suspended');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to suspend');
    } finally {
      setActionLoading(false);
    }
  };

  const handleUnsuspendUser = async (userId) => {
    if (!isPlatformOwner) {
      toast.error('Only Platform Owner can unsuspend users');
      return;
    }
    setActionLoading(true);
    try {
      await api.post(`/admin/users/${userId}/unsuspend`);
      toast.success('User unsuspended');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unsuspend');
    } finally {
      setActionLoading(false);
    }
  };

  const handleViewEvidence = async (chargeId, itemId) => {
    try {
      const res = await api.get(`/admin/charges/${chargeId}/evidence/${itemId}`);
      setEvidenceUrls(res.data.evidence_urls || []);
      setShowEvidenceModal(true);
    } catch (error) {
      toast.error('Failed to load evidence');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const LoadingSpinner = () => (
    <div className="flex justify-center py-8 sm:py-12">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#004B8D]"></div>
    </div>
  );

  // P3 ROLE-BASED ACCESS: Determine current admin role
  const adminRole = getAdminRole();
  const isSuperAdminLevel = adminRole === 'super_admin';
  
  // P3: Role display mapping
  const getRoleBadge = () => {
    if (adminRole === 'super_admin') {
      return { label: 'Super Admin', className: 'bg-purple-100 text-purple-700 border-purple-200' };
    }
    if (adminRole === 'admin') {
      return { label: 'Admin', className: 'bg-blue-100 text-blue-700 border-blue-200' };
    }
    return { label: 'Staff', className: 'bg-slate-100 text-slate-700 border-slate-200' };
  };
  const roleBadge = getRoleBadge();

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-100 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-3 sm:py-4 flex items-center justify-between">
          <div className="flex items-center gap-3 sm:gap-4">
            <Link to="/">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-[#004B8D] rounded-xl flex items-center justify-center">
                <Car className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
            </Link>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h1 className="font-heading text-base sm:text-xl font-semibold truncate">Admin Dashboard</h1>
                {/* P3: Role Badge */}
                <Badge className={`text-xs hidden sm:inline-flex ${roleBadge.className}`} data-testid="admin-role-badge">
                  {roleBadge.label}
                </Badge>
              </div>
              <div className="flex items-center gap-2">
                <p className="text-xs sm:text-sm text-slate-500 truncate hidden xs:block">{user?.email}</p>
                {/* P3: Mobile role badge */}
                <Badge className={`text-[10px] sm:hidden ${roleBadge.className}`}>
                  {roleBadge.label}
                </Badge>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            {/* Desktop buttons - P3: Super Admin Only Links */}
            <div className="hidden sm:flex items-center gap-3">
              {isSuperAdminLevel && (
                <>
                  <Link to="/admin/host-financials">
                    <Button variant="outline" size="sm">Host Financials</Button>
                  </Link>
                  <Link to="/admin/agreements">
                    <Button variant="outline" size="sm">Agreements</Button>
                  </Link>
                  <Link to="/admin/claims">
                    <Button variant="outline" size="sm">Claims</Button>
                  </Link>
                  <Link to="/platform-owner">
                    <Button variant="outline" size="sm">Platform Owner</Button>
                  </Link>
                </>
              )}
              {isSuperAdminLevel && (
                <Link to="/super-admin">
                  <Button variant="outline" size="sm">Super Admin</Button>
                </Link>
              )}
              <Button variant="ghost" size="sm" onClick={handleLogout} className="text-red-600">
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
            {/* Mobile menu button */}
            <Button 
              variant="ghost" 
              size="icon" 
              className="sm:hidden h-9 w-9"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              <Menu className="w-5 h-5" />
            </Button>
          </div>
        </div>
        {/* Mobile menu dropdown - P3: Role-based visibility */}
        {mobileMenuOpen && (
          <div className="sm:hidden border-t border-slate-100 bg-white px-4 py-3 space-y-2">
            {isSuperAdminLevel && (
              <>
                <Link to="/platform-owner" className="block">
                  <Button variant="outline" size="sm" className="w-full justify-start">Platform Owner</Button>
                </Link>
                <Link to="/super-admin" className="block">
                  <Button variant="outline" size="sm" className="w-full justify-start">Super Admin</Button>
                </Link>
              </>
            )}
            <Button variant="ghost" size="sm" onClick={handleLogout} className="w-full justify-start text-red-600">
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        )}
      </header>

      <div className="max-w-7xl mx-auto px-4 py-4 sm:py-6 lg:py-8">
        {/* Stats - Mobile Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 sm:gap-4 mb-6 sm:mb-8">
          <Card 
            className="border-slate-100 cursor-pointer hover:shadow-md transition-shadow" 
            onClick={() => setActiveTab('verifications')}
          >
            <CardContent className="p-4 sm:p-6">
              <Shield className="w-5 h-5 sm:w-6 sm:h-6 text-yellow-600 mb-2" />
              <p className="text-xl sm:text-2xl font-bold">{verifications.length}</p>
              <p className="text-xs sm:text-sm text-slate-500">Pending Verifications</p>
            </CardContent>
          </Card>
          <Card 
            className="border-slate-100 cursor-pointer hover:shadow-md transition-shadow" 
            onClick={() => setActiveTab('charges')}
            data-testid="pending-charges-stat-card"
          >
            <CardContent className="p-4 sm:p-6">
              <DollarSign className="w-5 h-5 sm:w-6 sm:h-6 text-amber-600 mb-2" />
              <p className="text-xl sm:text-2xl font-bold">{pendingCharges.length}</p>
              <p className="text-xs sm:text-sm text-slate-500">Pending Charges</p>
            </CardContent>
          </Card>
          <Card 
            className="border-slate-100 cursor-pointer hover:shadow-md transition-shadow" 
            onClick={() => setActiveTab('users')}
          >
            <CardContent className="p-4 sm:p-6">
              <Users className="w-5 h-5 sm:w-6 sm:h-6 text-[#004B8D] mb-2" />
              <p className="text-xl sm:text-2xl font-bold">{users.length}</p>
              <p className="text-xs sm:text-sm text-slate-500">Total Users</p>
            </CardContent>
          </Card>
          <Card 
            className="border-slate-100 cursor-pointer hover:shadow-md transition-shadow" 
            onClick={() => setActiveTab('bookings')}
          >
            <CardContent className="p-4 sm:p-6">
              <Calendar className="w-5 h-5 sm:w-6 sm:h-6 text-green-600 mb-2" />
              <p className="text-xl sm:text-2xl font-bold">{bookings.length}</p>
              <p className="text-xs sm:text-sm text-slate-500">Bookings</p>
            </CardContent>
          </Card>
          <Card 
            className="border-slate-100 cursor-pointer hover:shadow-md transition-shadow" 
            onClick={() => setActiveTab('disputes')}
          >
            <CardContent className="p-4 sm:p-6">
              <AlertTriangle className="w-5 h-5 sm:w-6 sm:h-6 text-red-600 mb-2" />
              <p className="text-xl sm:text-2xl font-bold">{disputes.length}</p>
              <p className="text-xs sm:text-sm text-slate-500">Open Disputes</p>
            </CardContent>
          </Card>
        </div>

        {/* P3: Super Admin Only - Platform Controls Section */}
        {isSuperAdminLevel && (
          <div className="mb-6 sm:mb-8" data-testid="super-admin-controls">
            <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2">
              <Shield className="w-4 h-4 text-purple-600" />
              Super Admin Controls
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Link to="/platform-owner" className="block">
                <Card className="border-purple-200 bg-purple-50/50 hover:shadow-md hover:-translate-y-0.5 transition-all cursor-pointer">
                  <CardContent className="p-3 sm:p-4">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
                        <Shield className="w-4 h-4 text-purple-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium">Platform Controls</p>
                        <p className="text-xs text-slate-500">Risk & Operations</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
              <Link to="/super-admin" className="block">
                <Card className="border-purple-200 bg-purple-50/50 hover:shadow-md hover:-translate-y-0.5 transition-all cursor-pointer">
                  <CardContent className="p-3 sm:p-4">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
                        <Users className="w-4 h-4 text-purple-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium">Admin Management</p>
                        <p className="text-xs text-slate-500">Roles & Permissions</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
              <Link to="/admin/claims" className="block">
                <Card className="border-purple-200 bg-purple-50/50 hover:shadow-md hover:-translate-y-0.5 transition-all cursor-pointer">
                  <CardContent className="p-3 sm:p-4">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
                        <FileText className="w-4 h-4 text-purple-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium">Claims Centre</p>
                        <p className="text-xs text-slate-500">Review & Settle</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
              <Link to="/admin/agreements" className="block">
                <Card className="border-purple-200 bg-purple-50/50 hover:shadow-md hover:-translate-y-0.5 transition-all cursor-pointer">
                  <CardContent className="p-3 sm:p-4">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
                        <FileText className="w-4 h-4 text-purple-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium">Legal / Governance</p>
                        <p className="text-xs text-slate-500">Agreements</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            </div>
          </div>
        )}

        {/* Tabs - Scrollable on mobile */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-4 sm:mb-6 w-full sm:w-auto overflow-x-auto flex">
            <TabsTrigger value="verifications" className="flex-1 sm:flex-none text-xs sm:text-sm">Verifications</TabsTrigger>
            <TabsTrigger value="charges" className="flex-1 sm:flex-none text-xs sm:text-sm" data-testid="charges-tab">Charges</TabsTrigger>
            <TabsTrigger value="collections" className="flex-1 sm:flex-none text-xs sm:text-sm" data-testid="collections-tab">Collections</TabsTrigger>
            <TabsTrigger value="users" className="flex-1 sm:flex-none text-xs sm:text-sm">Users</TabsTrigger>
            <TabsTrigger value="bookings" className="flex-1 sm:flex-none text-xs sm:text-sm">Bookings</TabsTrigger>
            <TabsTrigger value="disputes" className="flex-1 sm:flex-none text-xs sm:text-sm">Disputes</TabsTrigger>
          </TabsList>

          {/* Verifications Tab */}
          <TabsContent value="verifications">
            <Card className="border-slate-100">
              <CardHeader className="p-4 sm:p-6 pb-2 sm:pb-4">
                <CardTitle className="font-heading text-base sm:text-lg">Pending Verifications</CardTitle>
              </CardHeader>
              <CardContent className="p-4 pt-0 sm:p-6 sm:pt-0">
                {loading ? <LoadingSpinner /> : verifications.length === 0 ? (
                  <p className="text-center py-8 sm:py-12 text-slate-500 text-sm">No pending verifications</p>
                ) : (
                  <div className="space-y-4">
                    {verifications.map((v) => (
                      <div key={v.verification_id} className="p-3 sm:p-4 border border-slate-200 rounded-xl">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-3 sm:mb-4">
                          <div className="min-w-0">
                            <p className="font-medium text-sm sm:text-base truncate">{v.user?.full_name || 'User'}</p>
                            <p className="text-xs sm:text-sm text-slate-500 truncate">{v.user?.email}</p>
                          </div>
                          <Badge className="bg-yellow-100 text-yellow-800 text-xs self-start sm:self-auto">Pending</Badge>
                        </div>
                        
                        {/* Documents - Grid for mobile */}
                        <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-3 sm:mb-4">
                          {v.licence_front && (
                            <div>
                              <p className="text-[10px] sm:text-xs text-slate-500 mb-1">Licence Front</p>
                              <img 
                                src={`${process.env.REACT_APP_BACKEND_URL}/api/admin/verifications/${v.user_id}/documents/licence_front`}
                                alt="Licence Front"
                                className="h-14 sm:h-20 w-full rounded-lg object-cover cursor-pointer"
                                onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/admin/verifications/${v.user_id}/documents/licence_front`, '_blank')}
                                onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling && (e.target.nextSibling.style.display = 'flex'); }}
                              />
                              <div className="hidden h-14 sm:h-20 w-full rounded-lg bg-slate-100 items-center justify-center text-xs text-slate-400">
                                Not available
                              </div>
                            </div>
                          )}
                          {v.licence_back && (
                            <div>
                              <p className="text-[10px] sm:text-xs text-slate-500 mb-1">Licence Back</p>
                              <img 
                                src={`${process.env.REACT_APP_BACKEND_URL}/api/admin/verifications/${v.user_id}/documents/licence_back`}
                                alt="Licence Back"
                                className="h-14 sm:h-20 w-full rounded-lg object-cover cursor-pointer"
                                onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/admin/verifications/${v.user_id}/documents/licence_back`, '_blank')}
                                onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling && (e.target.nextSibling.style.display = 'flex'); }}
                              />
                              <div className="hidden h-14 sm:h-20 w-full rounded-lg bg-slate-100 items-center justify-center text-xs text-slate-400">
                                Not available
                              </div>
                            </div>
                          )}
                          {v.selfie && (
                            <div>
                              <p className="text-[10px] sm:text-xs text-slate-500 mb-1">Selfie</p>
                              <img 
                                src={`${process.env.REACT_APP_BACKEND_URL}/api/admin/verifications/${v.user_id}/documents/selfie`}
                                alt="Selfie"
                                className="h-14 sm:h-20 w-full rounded-lg object-cover cursor-pointer"
                                onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/admin/verifications/${v.user_id}/documents/selfie`, '_blank')}
                                onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling && (e.target.nextSibling.style.display = 'flex'); }}
                              />
                              <div className="hidden h-14 sm:h-20 w-full rounded-lg bg-slate-100 items-center justify-center text-xs text-slate-400">
                                Not available
                              </div>
                            </div>
                          )}
                        </div>

                        <div className="flex gap-2">
                          <Button
                            onClick={() => handleApproveVerification(v.verification_id)}
                            className="flex-1 bg-green-600 hover:bg-green-700 h-9 sm:h-10 text-xs sm:text-sm"
                            disabled={actionLoading}
                            data-testid={`approve-${v.verification_id}`}
                          >
                            <CheckCircle className="w-4 h-4 mr-1 sm:mr-2" />
                            Approve
                          </Button>
                          <Button
                            variant="destructive"
                            className="flex-1 h-9 sm:h-10 text-xs sm:text-sm"
                            onClick={() => { setSelectedItem(v); setShowRejectModal(true); }}
                            disabled={actionLoading}
                          >
                            <X className="w-4 h-4 mr-1 sm:mr-2" />
                            Reject
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Users Tab - Mobile Cards instead of Table */}
          <TabsContent value="users">
            <Card className="border-slate-100">
              <CardHeader className="p-4 sm:p-6 pb-2 sm:pb-4">
                <CardTitle className="font-heading text-base sm:text-lg">All Users</CardTitle>
              </CardHeader>
              <CardContent className="p-4 pt-0 sm:p-6 sm:pt-0">
                {loading ? <LoadingSpinner /> : users.length === 0 ? (
                  <p className="text-center py-8 sm:py-12 text-slate-500 text-sm">No users found</p>
                ) : (
                  <>
                    {/* Mobile Card View */}
                    <div className="sm:hidden space-y-3">
                      {users.map((u) => (
                        <div key={u.user_id} className="p-3 border border-slate-200 rounded-xl">
                          <div className="flex items-start justify-between mb-2">
                            <div className="min-w-0 flex-1">
                              <p className="font-medium text-sm truncate">{u.full_name}</p>
                              <p className="text-xs text-slate-500 truncate">{u.email}</p>
                            </div>
                            <Badge className={`text-[10px] ml-2 ${
                              u.verification_status === 'verified' ? 'bg-green-100 text-green-800' :
                              u.verification_status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-slate-100 text-slate-800'
                            }`}>
                              {u.verification_status}
                            </Badge>
                          </div>
                          <div className="flex gap-1 flex-wrap">
                            {u.roles?.filter(r => r.is_active).map((r, i) => (
                              <Badge key={i} variant="outline" className="text-[10px]">
                                {r.role}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    {/* Desktop Table View */}
                    <div className="hidden sm:block overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left py-3 px-4 font-medium text-sm">Name</th>
                            <th className="text-left py-3 px-4 font-medium text-sm">Email</th>
                            <th className="text-left py-3 px-4 font-medium text-sm">Phone</th>
                            <th className="text-left py-3 px-4 font-medium text-sm">Roles</th>
                            <th className="text-left py-3 px-4 font-medium text-sm">Status</th>
                            <th className="text-left py-3 px-4 font-medium text-sm">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {users.map((u) => (
                            <tr 
                              key={u.user_id} 
                              className="border-b hover:bg-slate-50 cursor-pointer"
                              onClick={() => { setSelectedUser(u); setShowUserDetails(true); }}
                              data-testid={`user-row-${u.user_id}`}
                            >
                              <td className="py-3 px-4 text-sm font-medium">{u.full_name}</td>
                              <td className="py-3 px-4 text-sm text-slate-500">{u.email}</td>
                              <td className="py-3 px-4 text-sm text-slate-500">{u.phone || '-'}</td>
                              <td className="py-3 px-4">
                                <div className="flex gap-1 flex-wrap">
                                  {u.roles?.filter(r => r.is_active).map((r, i) => (
                                    <Badge key={i} variant="outline" className="text-xs">
                                      {r.role}
                                    </Badge>
                                  ))}
                                </div>
                              </td>
                              <td className="py-3 px-4">
                                <Badge className={`text-xs ${
                                  u.verification_status === 'verified' ? 'bg-green-100 text-green-800' :
                                  u.verification_status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                  'bg-slate-100 text-slate-800'
                                }`}>
                                  {u.verification_status}
                                </Badge>
                              </td>
                              <td className="py-3 px-4">
                                <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                                  {u.phone && (
                                    <a href={`tel:${u.phone}`} title="Call User">
                                      <Button size="sm" variant="outline" className="h-8 w-8 p-0">
                                        <Phone className="w-4 h-4 text-green-600" />
                                      </Button>
                                    </a>
                                  )}
                                  <a href={`mailto:${u.email}`} title="Email User">
                                    <Button size="sm" variant="outline" className="h-8 w-8 p-0">
                                      <Mail className="w-4 h-4 text-blue-600" />
                                    </Button>
                                  </a>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Bookings Tab */}
          <TabsContent value="bookings">
            <Card className="border-slate-100">
              <CardHeader className="p-4 sm:p-6 pb-2 sm:pb-4">
                <CardTitle className="font-heading text-base sm:text-lg">All Bookings</CardTitle>
              </CardHeader>
              <CardContent className="p-4 pt-0 sm:p-6 sm:pt-0">
                {loading ? <LoadingSpinner /> : bookings.length === 0 ? (
                  <p className="text-center py-8 sm:py-12 text-slate-500 text-sm">No bookings found</p>
                ) : (
                  <div className="space-y-3">
                    {bookings.map((b) => (
                      <Link key={b.booking_id} to={`/bookings/${b.booking_id}`}>
                        <div className="p-3 sm:p-4 border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors">
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                            <div className="min-w-0">
                              <p className="font-medium text-sm sm:text-base truncate">
                                {b.vehicle?.make} {b.vehicle?.model}
                              </p>
                              <p className="text-xs sm:text-sm text-slate-500 truncate">
                                {b.customer?.full_name} • {b.start_date} to {b.end_date}
                              </p>
                            </div>
                            <div className="flex items-center justify-between sm:flex-col sm:items-end gap-2">
                              <Badge className={`text-xs ${
                                b.status === 'completed' ? 'bg-slate-100 text-slate-800' :
                                b.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                                b.status === 'active' || b.status === 'trip_active' ? 'bg-emerald-100 text-emerald-800' :
                                b.status === 'requested' ? 'bg-yellow-100 text-yellow-800' :
                                b.status === 'approved_pending_payment' ? 'bg-blue-100 text-blue-800' :
                                b.status === 'paid' ? 'bg-green-100 text-green-800' :
                                b.status === 'posthire_in_progress' ? 'bg-orange-100 text-orange-800' :
                                b.status === 'posthire_submitted' || b.status === 'cooldown_window_open' ? 'bg-purple-100 text-purple-800' :
                                'bg-slate-100 text-slate-800'
                              }`}>
                                {b.status === 'posthire_in_progress' ? 'Return In Progress' :
                                 b.status === 'posthire_submitted' ? 'Review Period' :
                                 b.status === 'cooldown_window_open' ? 'Review Period' :
                                 b.status === 'approved_pending_payment' ? 'Awaiting Payment' :
                                 b.status === 'active' || b.status === 'trip_active' ? 'Trip Active' :
                                 b.status?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Unknown'}
                              </Badge>
                              <p className="text-xs sm:text-sm text-slate-500">${b.total_amount || (b.subtotal + (b.renter_fee_amount || 0)).toFixed(2)} AUD</p>
                            </div>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Disputes Tab */}
          <TabsContent value="disputes">
            <Card className="border-slate-100">
              <CardHeader className="p-4 sm:p-6 pb-2 sm:pb-4">
                <CardTitle className="font-heading text-base sm:text-lg">Disputes</CardTitle>
              </CardHeader>
              <CardContent className="p-4 pt-0 sm:p-6 sm:pt-0">
                {loading ? <LoadingSpinner /> : disputes.length === 0 ? (
                  <p className="text-center py-8 sm:py-12 text-slate-500 text-sm">No disputes</p>
                ) : (
                  <div className="space-y-3">
                    {disputes.map((d) => (
                      <div key={d.dispute_id} className="p-3 sm:p-4 border border-slate-200 rounded-xl">
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <p className="font-medium text-sm sm:text-base">Dispute #{d.dispute_id.slice(0, 8)}</p>
                          <Badge className={`text-xs ${d.status === 'open' ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}`}>
                            {d.status}
                          </Badge>
                        </div>
                        <p className="text-xs sm:text-sm text-slate-600 mb-2 line-clamp-2">{d.description}</p>
                        <p className="text-[10px] sm:text-xs text-slate-500">
                          Raised by: {d.raised_by_user?.full_name} • Booking: {d.booking_id.slice(0, 8)}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* P0 Part 3: Pending Charge Approvals Tab */}
          <TabsContent value="charges" data-testid="charges-tab-content">
            <Card className="border-slate-100">
              <CardHeader className="p-4 sm:p-6 pb-2 sm:pb-4">
                <CardTitle className="font-heading text-base sm:text-lg flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-amber-600" />
                  Pending Charge Approvals
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 pt-0 sm:p-6 sm:pt-0">
                {loading ? <LoadingSpinner /> : pendingCharges.length === 0 ? (
                  <p className="text-center py-8 sm:py-12 text-slate-500 text-sm">No charges pending approval</p>
                ) : (
                  <div className="space-y-4">
                    {pendingCharges.map((charge) => (
                      <div key={charge.charge_id} className="p-4 border border-slate-200 rounded-xl bg-amber-50/30" data-testid={`charge-${charge.charge_id}`}>
                        {/* Header */}
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-4">
                          <div>
                            <p className="font-medium text-sm sm:text-base">
                              Booking: {charge.booking_id?.slice(0, 12)}...
                            </p>
                            <p className="text-xs text-slate-500">
                              Owner: {charge.owner?.full_name} • Renter: {charge.renter?.full_name}
                            </p>
                            {charge.vehicle && (
                              <p className="text-xs text-slate-500">
                                {charge.vehicle.year} {charge.vehicle.make} {charge.vehicle.model}
                              </p>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge className="bg-amber-100 text-amber-800 text-xs">
                              {charge.pending_count || 0} pending
                            </Badge>
                            <Badge className="bg-slate-100 text-slate-800 text-xs">
                              ${(charge.subtotal_cents / 100).toFixed(2)} total
                            </Badge>
                          </div>
                        </div>

                        {/* Pending Items */}
                        <div className="space-y-2 mb-4">
                          {charge.pending_items?.map((item) => (
                            <div key={item.item_id} className="p-3 bg-white rounded-lg border border-amber-200" data-testid={`charge-item-${item.item_id}`}>
                              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-1">
                                    <Badge className={`text-xs ${
                                      item.charge_type === 'damage_estimate' ? 'bg-red-100 text-red-800' :
                                      item.charge_type === 'toll_charges' ? 'bg-cyan-100 text-cyan-800' :
                                      item.charge_type === 'cleaning_fee' ? 'bg-pink-100 text-pink-800' :
                                      'bg-slate-100 text-slate-800'
                                    }`}>
                                      {CHARGE_TYPE_LABELS[item.charge_type] || item.charge_type}
                                    </Badge>
                                    <span className="font-bold text-sm">${(item.amount_cents / 100).toFixed(2)}</span>
                                    {item.revision_number > 0 && (
                                      <Badge className="bg-purple-100 text-purple-800 text-xs">
                                        Rev {item.revision_number}
                                      </Badge>
                                    )}
                                  </div>
                                  <p className="text-xs text-slate-600 line-clamp-2">{item.description}</p>
                                  {item.admin_review_reason && (
                                    <p className="text-xs text-amber-600 mt-1">
                                      Why review: {item.admin_review_reason}
                                    </p>
                                  )}
                                </div>
                                <div className="flex items-center gap-2 mt-2 sm:mt-0">
                                  {item.evidence_s3_keys?.length > 0 && (
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => handleViewEvidence(charge.charge_id, item.item_id)}
                                      className="h-8 text-xs"
                                      data-testid={`view-evidence-${item.item_id}`}
                                    >
                                      <Eye className="w-3 h-3 mr-1" />
                                      Evidence ({item.evidence_s3_keys.length})
                                    </Button>
                                  )}
                                  <Button
                                    onClick={() => handleApproveChargeItem(charge.charge_id, item.item_id)}
                                    className="bg-green-600 hover:bg-green-700 h-8 text-xs"
                                    disabled={actionLoading}
                                    data-testid={`approve-item-${item.item_id}`}
                                  >
                                    <CheckCircle className="w-3 h-3 mr-1" />
                                    Approve
                                  </Button>
                                  <Button
                                    variant="destructive"
                                    className="h-8 text-xs"
                                    onClick={() => {
                                      setSelectedCharge(charge);
                                      setSelectedChargeItem(item);
                                      setShowChargeRejectModal(true);
                                    }}
                                    disabled={actionLoading}
                                    data-testid={`reject-item-${item.item_id}`}
                                  >
                                    <X className="w-3 h-3 mr-1" />
                                    Reject
                                  </Button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* Bulk Actions */}
                        {charge.pending_count > 1 && (
                          <div className="pt-3 border-t border-slate-200">
                            <Button
                              onClick={() => handleApproveAllCharges(charge.charge_id)}
                              className="bg-green-600 hover:bg-green-700 w-full sm:w-auto text-sm"
                              disabled={actionLoading}
                              data-testid={`approve-all-${charge.charge_id}`}
                            >
                              <CheckCircle className="w-4 h-4 mr-2" />
                              Approve All ({charge.pending_count} items)
                            </Button>
                          </div>
                        )}

                        {/* Part 4: Payment Execution Section */}
                        {charge.charge_execution_status && (
                          <div className="pt-3 border-t border-slate-200">
                            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
                              <div>
                                <p className="text-xs font-medium text-slate-700">Payment Status:</p>
                                <Badge className={`text-xs ${
                                  charge.charge_execution_status === 'paid' ? 'bg-emerald-100 text-emerald-800' :
                                  charge.charge_execution_status === 'payment_pending' ? 'bg-blue-100 text-blue-800' :
                                  charge.charge_execution_status === 'payment_failed' ? 'bg-red-100 text-red-800' :
                                  charge.charge_execution_status === 'approved' ? 'bg-green-100 text-green-800' :
                                  'bg-slate-100 text-slate-800'
                                }`}>
                                  {charge.charge_execution_status.replace(/_/g, ' ')}
                                </Badge>
                                {charge.stripe_payment_intent_id && (
                                  <p className="text-xs text-slate-500 mt-1">
                                    PI: {charge.stripe_payment_intent_id.slice(0, 20)}...
                                  </p>
                                )}
                                {charge.last_payment_error_message && (
                                  <p className="text-xs text-red-600 mt-1">
                                    Error: {charge.last_payment_error_message}
                                  </p>
                                )}
                              </div>
                              <div className="flex gap-2">
                                {(charge.charge_execution_status === 'approved' && charge.pending_count === 0) && (
                                  <Button
                                    onClick={() => handleExecutePayment(charge.charge_id)}
                                    className="bg-blue-600 hover:bg-blue-700 text-xs h-8"
                                    disabled={actionLoading}
                                    data-testid={`execute-payment-${charge.charge_id}`}
                                  >
                                    <DollarSign className="w-3 h-3 mr-1" />
                                    Execute Payment
                                  </Button>
                                )}
                                {charge.charge_execution_status === 'payment_failed' && (
                                  <Button
                                    onClick={() => handleRetryPayment(charge.charge_id)}
                                    className="bg-amber-600 hover:bg-amber-700 text-xs h-8"
                                    disabled={actionLoading}
                                    data-testid={`retry-payment-${charge.charge_id}`}
                                  >
                                    <DollarSign className="w-3 h-3 mr-1" />
                                    Retry Payment
                                  </Button>
                                )}
                                {charge.charge_execution_status === 'paid' && charge.payment_receipt_url && (
                                  <a 
                                    href={charge.payment_receipt_url} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline"
                                  >
                                    <FileText className="w-3 h-3" />
                                    View Receipt
                                  </a>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Collections Tab - Part 5 */}
          <TabsContent value="collections" data-testid="collections-tab-content">
            {/* Collections Summary KPIs */}
            {collectionsSummary && (
              <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-4">
                <Card className="border-slate-100">
                  <CardContent className="p-3">
                    <p className="text-xs text-slate-500">Total Unpaid</p>
                    <p className="text-lg font-bold text-red-600">${collectionsSummary.total_unpaid_dollars?.toFixed(2) || '0.00'}</p>
                  </CardContent>
                </Card>
                <Card className="border-slate-100">
                  <CardContent className="p-3">
                    <p className="text-xs text-slate-500">Retrying</p>
                    <p className="text-lg font-bold text-amber-600">{collectionsSummary.retrying?.count || 0}</p>
                  </CardContent>
                </Card>
                <Card className="border-slate-100">
                  <CardContent className="p-3">
                    <p className="text-xs text-slate-500">Final Failed</p>
                    <p className="text-lg font-bold text-red-600">{collectionsSummary.failed_final?.count || 0}</p>
                  </CardContent>
                </Card>
                <Card className="border-slate-100">
                  <CardContent className="p-3">
                    <p className="text-xs text-slate-500">Needs Action</p>
                    <p className="text-lg font-bold text-blue-600">{collectionsSummary.needs_action?.count || 0}</p>
                  </CardContent>
                </Card>
                <Card className="border-purple-200 bg-purple-50">
                  <CardContent className="p-3">
                    <p className="text-xs text-purple-600">Disputed (Frozen)</p>
                    <p className="text-lg font-bold text-purple-700">{collectionsSummary.frozen_dispute?.count || 0}</p>
                    {collectionsSummary.frozen_dispute?.total_dollars > 0 && (
                      <p className="text-xs text-purple-500">${collectionsSummary.frozen_dispute.total_dollars.toFixed(2)}</p>
                    )}
                  </CardContent>
                </Card>
                <Card className="border-slate-100">
                  <CardContent className="p-3">
                    <p className="text-xs text-slate-500">Users Suspended</p>
                    <p className="text-lg font-bold text-slate-800">{collectionsSummary.users_suspended || 0}</p>
                  </CardContent>
                </Card>
              </div>
            )}

            <Card className="border-slate-100">
              <CardHeader className="p-4 sm:p-6 pb-2 sm:pb-4">
                <CardTitle className="font-heading text-base sm:text-lg">Failed / Retrying Charges</CardTitle>
              </CardHeader>
              <CardContent className="p-4 pt-0 sm:p-6 sm:pt-0">
                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-600" />
                  </div>
                ) : failedCharges.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <DollarSign className="w-10 h-10 mx-auto mb-2 opacity-30" />
                    <p className="text-sm">No failed charges</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {failedCharges.map((charge) => (
                      <div key={charge.charge_id} className="border border-slate-200 rounded-lg p-3 sm:p-4 bg-white">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-3">
                          <div>
                            <p className="font-medium text-sm">
                              {charge.renter?.full_name || 'Unknown Renter'}
                              <span className="text-slate-400 ml-2 text-xs">{charge.renter?.email}</span>
                            </p>
                            <p className="text-xs text-slate-500">Booking: {charge.booking_id?.slice(0, 12)}...</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge className={`text-xs ${
                              charge.collection_status === 'failed_retrying' ? 'bg-amber-100 text-amber-800' :
                              charge.collection_status === 'failed_final' ? 'bg-red-100 text-red-800' :
                              charge.collection_status === 'needs_action' ? 'bg-blue-100 text-blue-800' :
                              charge.collection_status === 'frozen_dispute' ? 'bg-purple-100 text-purple-800' :
                              'bg-slate-100 text-slate-800'
                            }`}>
                              {charge.collection_status?.replace(/_/g, ' ')}
                            </Badge>
                            <span className="font-bold text-red-600">${(charge.subtotal_cents / 100).toFixed(2)}</span>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs mb-3">
                          <div>
                            <span className="text-slate-500">Retry #:</span>
                            <span className="ml-1 font-medium">{charge.retry_count || 0}</span>
                          </div>
                          <div>
                            <span className="text-slate-500">Next retry:</span>
                            <span className="ml-1 font-medium">
                              {charge.next_retry_at ? new Date(charge.next_retry_at).toLocaleDateString() : 'N/A'}
                            </span>
                          </div>
                          <div>
                            <span className="text-slate-500">Error:</span>
                            <span className="ml-1 font-medium text-red-600">{charge.last_payment_error_code || 'N/A'}</span>
                          </div>
                          <div>
                            <span className="text-slate-500">Escalation:</span>
                            <span className="ml-1 font-medium">{charge.escalation_status || 'none'}</span>
                          </div>
                        </div>

                        {/* Part 6: Dispute Banner */}
                        {charge.frozen_for_dispute && (
                          <div className="mb-3 p-2 bg-purple-50 border border-purple-200 rounded-lg">
                            <div className="flex items-center gap-2 mb-1">
                              <AlertTriangle className="w-4 h-4 text-purple-600" />
                              <span className="font-medium text-purple-800 text-xs">DISPUTED - Execution Frozen</span>
                              {charge.dispute_status && (
                                <Badge className="text-[10px] bg-purple-100 text-purple-700">{charge.dispute_status}</Badge>
                              )}
                            </div>
                            {charge.dispute_freeze_reason && (
                              <p className="text-xs text-purple-700">{charge.dispute_freeze_reason}</p>
                            )}
                            {charge.dispute_id && (
                              <p className="text-[10px] text-purple-500 mt-1">Dispute ID: {charge.dispute_id}</p>
                            )}
                          </div>
                        )}

                        {charge.last_payment_error_message && (
                          <p className="text-xs text-red-600 mb-3 bg-red-50 p-2 rounded">
                            {charge.last_payment_error_message}
                          </p>
                        )}

                        <div className="flex flex-wrap gap-2">
                          {charge.collection_status !== 'frozen_dispute' && (
                            <Button
                              size="sm"
                              onClick={() => handleCollectionRetryNow(charge.charge_id)}
                              disabled={actionLoading}
                              className="bg-amber-600 hover:bg-amber-700 text-xs h-7"
                              data-testid={`retry-now-${charge.charge_id}`}
                            >
                              Retry Now
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleMarkResolved(charge.charge_id)}
                            disabled={actionLoading}
                            className="text-xs h-7"
                          >
                            Mark Resolved
                          </Button>
                          {charge.collection_status !== 'frozen_dispute' ? (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleFreezeForDispute(charge.charge_id)}
                              disabled={actionLoading}
                              className="text-xs h-7 text-purple-600"
                            >
                              Freeze (Dispute)
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleUnfreezeCharge(charge.charge_id)}
                              disabled={actionLoading}
                              className="text-xs h-7 text-green-600"
                            >
                              Unfreeze
                            </Button>
                          )}
                          {isPlatformOwner && charge.collection_status === 'failed_final' && (
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => handleSendToCollections(charge.charge_id)}
                              disabled={actionLoading}
                              className="text-xs h-7"
                            >
                              Send to Collections
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Reject Modal */}
      <Dialog open={showRejectModal} onOpenChange={setShowRejectModal}>
        <DialogContent className="mx-4 max-w-[calc(100vw-2rem)] sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-base sm:text-lg">Reject Verification</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Reason for rejection (will be sent to user)..."
              rows={3}
              className="text-sm"
            />
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setShowRejectModal(false)} className="w-full sm:w-auto">
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleRejectVerification} disabled={actionLoading} className="w-full sm:w-auto">
              {actionLoading ? 'Rejecting...' : 'Reject'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* P0 Part 3: Charge Rejection Modal */}
      <Dialog open={showChargeRejectModal} onOpenChange={setShowChargeRejectModal}>
        <DialogContent className="mx-4 max-w-[calc(100vw-2rem)] sm:max-w-md" data-testid="charge-reject-modal">
          <DialogHeader>
            <DialogTitle className="text-base sm:text-lg">Reject Charge Item</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-3">
            {selectedChargeItem && (
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-sm font-medium">
                  {CHARGE_TYPE_LABELS[selectedChargeItem.charge_type] || selectedChargeItem.charge_type}
                </p>
                <p className="text-lg font-bold text-red-600">
                  ${(selectedChargeItem.amount_cents / 100).toFixed(2)}
                </p>
                <p className="text-xs text-slate-600 mt-1">{selectedChargeItem.description}</p>
              </div>
            )}
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Rejection Reason (required)</label>
              <Textarea
                value={chargeRejectReason}
                onChange={(e) => setChargeRejectReason(e.target.value)}
                placeholder="Explain why this charge is being rejected (min 10 characters)..."
                rows={3}
                className="text-sm"
                data-testid="charge-reject-reason"
              />
              <p className="text-xs text-slate-500 mt-1">
                Owner will be notified and can re-submit with additional evidence
              </p>
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => {
              setShowChargeRejectModal(false);
              setChargeRejectReason('');
            }} className="w-full sm:w-auto">
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleRejectChargeItem} 
              disabled={actionLoading || chargeRejectReason.length < 10} 
              className="w-full sm:w-auto"
              data-testid="confirm-charge-reject"
            >
              {actionLoading ? 'Rejecting...' : 'Reject Charge'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* P0 Part 3: Evidence Viewer Modal */}
      <Dialog open={showEvidenceModal} onOpenChange={setShowEvidenceModal}>
        <DialogContent className="mx-4 max-w-[calc(100vw-2rem)] sm:max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="evidence-modal">
          <DialogHeader>
            <DialogTitle className="text-base sm:text-lg flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Evidence Files
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {evidenceUrls.map((evidence, idx) => (
              <div key={idx} className="border border-slate-200 rounded-lg overflow-hidden">
                {evidence.url ? (
                  evidence.filename?.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
                    <img 
                      src={evidence.url} 
                      alt={evidence.filename}
                      className="w-full h-48 object-cover"
                    />
                  ) : (
                    <a 
                      href={evidence.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="block p-4 bg-slate-50 hover:bg-slate-100 transition-colors"
                    >
                      <FileText className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                      <p className="text-sm text-center truncate">{evidence.filename}</p>
                    </a>
                  )
                ) : (
                  <div className="p-4 bg-red-50 text-center">
                    <p className="text-xs text-red-600">Failed to load: {evidence.error}</p>
                  </div>
                )}
                <div className="p-2 bg-slate-50 border-t">
                  <p className="text-xs text-slate-500 truncate">{evidence.filename}</p>
                </div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEvidenceModal(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* User Details Dialog */}
      <Dialog open={showUserDetails} onOpenChange={setShowUserDetails}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="w-5 h-5 text-[#004B8D]" />
              User Details
            </DialogTitle>
          </DialogHeader>
          {selectedUser && (
            <div className="space-y-4">
              {/* Basic Info */}
              <div className="bg-slate-50 rounded-lg p-4">
                <h4 className="font-semibold text-lg">{selectedUser.full_name || 'Unknown'}</h4>
                <p className="text-sm text-slate-500">{selectedUser.email}</p>
                {selectedUser.phone && (
                  <p className="text-sm text-slate-500 mt-1">{selectedUser.phone}</p>
                )}
              </div>
              
              {/* Status & Roles */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-slate-500 mb-1">Verification Status</p>
                  <Badge className={`${
                    selectedUser.verification_status === 'verified' ? 'bg-green-100 text-green-800' :
                    selectedUser.verification_status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-slate-100 text-slate-800'
                  }`}>
                    {selectedUser.verification_status || 'unverified'}
                  </Badge>
                </div>
                <div>
                  <p className="text-xs text-slate-500 mb-1">Account State</p>
                  <Badge className={selectedUser.is_active !== false ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                    {selectedUser.is_active !== false ? 'Active' : 'Suspended'}
                  </Badge>
                </div>
              </div>
              
              {/* Roles */}
              <div>
                <p className="text-xs text-slate-500 mb-2">Roles</p>
                <div className="flex flex-wrap gap-2">
                  {selectedUser.roles?.filter(r => r.is_active).map((r, i) => (
                    <Badge key={i} variant="outline">{r.role}</Badge>
                  ))}
                  {(!selectedUser.roles || selectedUser.roles.filter(r => r.is_active).length === 0) && (
                    <span className="text-sm text-slate-400">No active roles</span>
                  )}
                </div>
              </div>
              
              {/* Host Status */}
              {selectedUser.roles?.some(r => r.role === 'host' && r.is_active) && (
                <div>
                  <p className="text-xs text-slate-500 mb-1">Host Status</p>
                  <Badge className="bg-blue-100 text-blue-800">Active Host</Badge>
                </div>
              )}

              {/* Subscription & Feature Flags */}
              <div className="border-t pt-3" data-testid="user-entitlements-section">
                <p className="text-xs text-slate-500 mb-2 font-medium">Subscription & Features</p>
                <div className="mb-3">
                  <label className="text-xs text-slate-500 block mb-1">Tier</label>
                  <select
                    data-testid="subscription-tier-select"
                    className="w-full border rounded-md px-2 py-1.5 text-sm bg-white"
                    value={selectedUser.subscription_tier || 'free'}
                    disabled={featureSaving}
                    onChange={(e) => updateUserFeatures(selectedUser.user_id, { subscription_tier: e.target.value })}
                  >
                    {['free', 'basic', 'pro', 'premium'].map(t => (
                      <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-1.5">
                  {['gps_tracking', 'priority_support', 'boost_listing', 'advanced_analytics', 'ai_assistant', 'insurance_perks'].map(flag => (
                    <label key={flag} className="flex items-center justify-between text-sm cursor-pointer" data-testid={`feature-flag-${flag}`}>
                      <span className="text-slate-700">{flag.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</span>
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-slate-300 text-blue-600 cursor-pointer"
                        checked={selectedUser.feature_flags?.[flag] || false}
                        disabled={featureSaving}
                        onChange={(e) => updateUserFeatures(selectedUser.user_id, { feature_flags: { [flag]: e.target.checked } })}
                      />
                    </label>
                  ))}
                </div>
              </div>
              
              {/* Contact Buttons */}
              <div className="flex gap-2 pt-2 border-t">
                {selectedUser.phone && (
                  <a href={`tel:${selectedUser.phone}`} className="flex-1">
                    <Button className="w-full bg-green-600 hover:bg-green-700" data-testid="call-user-btn">
                      <Phone className="w-4 h-4 mr-2" />
                      Call User
                    </Button>
                  </a>
                )}
                <a href={`mailto:${selectedUser.email}`} className="flex-1">
                  <Button variant="outline" className="w-full">
                    <Mail className="w-4 h-4 mr-2" />
                    Email
                  </Button>
                </a>
              </div>
              
              {/* View Bookings Link */}
              <Link 
                to={`/admin?tab=bookings&user=${selectedUser.user_id}`}
                onClick={() => setShowUserDetails(false)}
                className="block"
              >
                <Button variant="outline" className="w-full">
                  <Calendar className="w-4 h-4 mr-2" />
                  View User's Bookings
                </Button>
              </Link>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUserDetails(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
